import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('dex_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getAdminAuthHeader = () => {
  const token = localStorage.getItem('dex_admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// User API
export const userAPI = {
  getProfile: () => axios.get(`${API}/user/profile`, { headers: getAuthHeader() }),
  getDashboard: () => axios.get(`${API}/user/dashboard`, { headers: getAuthHeader() }),
  changePassword: (data) => axios.post(`${API}/user/change-password`, data, { headers: getAuthHeader() }),
};

// Deposits API
export const depositsAPI = {
  getWallets: () => axios.get(`${API}/deposits/wallets`, { headers: getAuthHeader() }),
  create: (data) => axios.post(`${API}/deposits/create`, data, { headers: getAuthHeader() }),
  uploadProof: (depositId, formData) => {
    return axios.post(`${API}/deposits/${depositId}/upload-proof`, formData, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
    });
  },
  getAll: () => axios.get(`${API}/deposits`, { headers: getAuthHeader() }),
};

// Withdrawals API
export const withdrawalsAPI = {
  create: (data) => axios.post(`${API}/withdrawals/create`, data, { headers: getAuthHeader() }),
  getAll: () => axios.get(`${API}/withdrawals`, { headers: getAuthHeader() }),
};

// Investments API
export const investmentsAPI = {
  create: (data) => axios.post(`${API}/investments/create`, data, { headers: getAuthHeader() }),
  getAll: () => axios.get(`${API}/investments`, { headers: getAuthHeader() }),
  getPlans: () => axios.get(`${API}/investments/plans`, { headers: getAuthHeader() }),
  getLiveProfit: () => axios.get(`${API}/investments/live-profit`, { headers: getAuthHeader() }),
};

// Referrals API
export const referralsAPI = {
  getStats: () => axios.get(`${API}/referrals/stats`, { headers: getAuthHeader() }),
  validateCode: (code) => axios.get(`${API}/referrals/validate/${code}`),
};

// Crypto Prices API
export const cryptoAPI = {
  getPrices: () => axios.get(`${API}/crypto/prices`),
  getHistory: (symbol) => axios.get(`${API}/crypto/history/${symbol}`),
};

// Notifications API
export const notificationsAPI = {
  getAll: () => axios.get(`${API}/notifications`, { headers: getAuthHeader() }),
  markRead: (id) => axios.put(`${API}/notifications/${id}/read`, {}, { headers: getAuthHeader() }),
  markAllRead: () => axios.put(`${API}/notifications/mark-all-read`, {}, { headers: getAuthHeader() }),
};

// Admin API
export const adminAPI = {
  getDashboard: () => axios.get(`${API}/admin/dashboard`, { headers: getAdminAuthHeader() }),
  
  // Users
  getUsers: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.search) queryParams.append('search', params.search);
    if (params.status) queryParams.append('status', params.status);
    const queryString = queryParams.toString();
    return axios.get(`${API}/admin/users${queryString ? `?${queryString}` : ''}`, { headers: getAdminAuthHeader() });
  },
  getUser: (id) => axios.get(`${API}/admin/users/${id}`, { headers: getAdminAuthHeader() }),
  updateUser: (id, data) => axios.put(`${API}/admin/users/${id}`, data, { headers: getAdminAuthHeader() }),
  updateUserFull: (id, data) => axios.put(`${API}/admin/users/${id}/full`, data, { headers: getAdminAuthHeader() }),
  deleteUser: (id) => axios.delete(`${API}/admin/users/${id}`, { headers: getAdminAuthHeader() }),
  adjustBalance: (id, data) => axios.post(`${API}/admin/users/${id}/adjust-balance`, data, { headers: getAdminAuthHeader() }),
  addProfit: (id, data) => axios.post(`${API}/admin/users/${id}/profit`, data, { headers: getAdminAuthHeader() }),
  addLoss: (id, data) => axios.post(`${API}/admin/users/${id}/loss`, data, { headers: getAdminAuthHeader() }),
  getUsersForProfit: () => axios.get(`${API}/admin/users-for-profit`, { headers: getAdminAuthHeader() }),
  
  // Deposits
  getDeposits: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.crypto_type) queryParams.append('crypto_type', params.crypto_type);
    const queryString = queryParams.toString();
    return axios.get(`${API}/admin/deposits${queryString ? `?${queryString}` : ''}`, { headers: getAdminAuthHeader() });
  },
  updateDeposit: (id, data) => axios.put(`${API}/admin/deposits/${id}`, data, { headers: getAdminAuthHeader() }),
  
  // Withdrawals
  getWithdrawals: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.crypto_type) queryParams.append('crypto_type', params.crypto_type);
    const queryString = queryParams.toString();
    return axios.get(`${API}/admin/withdrawals${queryString ? `?${queryString}` : ''}`, { headers: getAdminAuthHeader() });
  },
  updateWithdrawal: (id, data) => axios.put(`${API}/admin/withdrawals/${id}`, data, { headers: getAdminAuthHeader() }),
  
  // Settings
  getSettings: () => axios.get(`${API}/admin/settings`, { headers: getAdminAuthHeader() }),
  updateSettings: (data) => axios.put(`${API}/admin/settings`, data, { headers: getAdminAuthHeader() }),
  
  // Profits
  distributeProfit: () => axios.post(`${API}/admin/profit/distribute`, {}, { headers: getAdminAuthHeader() }),
  simulateProfit: () => axios.get(`${API}/admin/profit/simulate`, { headers: getAdminAuthHeader() }),
  manualProfitLoss: (data) => axios.post(`${API}/admin/profit/manual`, data, { headers: getAdminAuthHeader() }),
  
  // Audit Logs
  getAuditLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.action_type) queryParams.append('action_type', params.action_type);
    if (params.limit) queryParams.append('limit', params.limit);
    const queryString = queryParams.toString();
    return axios.get(`${API}/admin/audit-logs${queryString ? `?${queryString}` : ''}`, { headers: getAdminAuthHeader() });
  },
  
  // Notifications
  sendNotification: (data) => axios.post(`${API}/admin/notifications/send`, data, { headers: getAdminAuthHeader() }),
  
  // Admin Notification Settings
  getNotificationSettings: () => axios.get(`${API}/admin/notification-settings`, { headers: getAdminAuthHeader() }),
  updateNotificationSettings: (data) => axios.put(`${API}/admin/notification-settings`, data, { headers: getAdminAuthHeader() }),
  
  // Activity Feed
  getActivityFeed: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.event_type) queryParams.append('event_type', params.event_type);
    if (params.limit) queryParams.append('limit', params.limit);
    const queryString = queryParams.toString();
    return axios.get(`${API}/admin/activity-feed${queryString ? `?${queryString}` : ''}`, { headers: getAdminAuthHeader() });
  },
  
  // Push Notifications
  getVapidPublicKey: () => axios.get(`${API}/admin/vapid-public-key`, { headers: getAdminAuthHeader() }),
  subscribeToPush: (subscription) => axios.post(`${API}/admin/push-subscribe`, subscription, { headers: getAdminAuthHeader() }),
  unsubscribeFromPush: (endpoint) => axios.delete(`${API}/admin/push-unsubscribe?endpoint=${encodeURIComponent(endpoint)}`, { headers: getAdminAuthHeader() }),
  testPushNotification: () => axios.post(`${API}/admin/test-push`, {}, { headers: getAdminAuthHeader() }),
};

// Events API (for user actions like wallet copy)
export const eventsAPI = {
  trackWalletCopy: (cryptoType) => axios.post(`${API}/events/wallet-copied?crypto_type=${cryptoType}`, {}, { headers: getAuthHeader() }),
};

export default {
  userAPI,
  depositsAPI,
  withdrawalsAPI,
  investmentsAPI,
  notificationsAPI,
  adminAPI,
  referralsAPI,
  cryptoAPI,
  eventsAPI,
};
